import { useTranslation } from 'react-i18next';
import type { Variant } from '@/types/domain';

// Sponsored placeholder banner for BuildHQ (buildhq.app) — built from the
// buildhq-ad-kit brief. Copy is from the kit's approved sets; do not invent
// new pricing/feature claims.
const AD = {
  brand: 'BuildHQ',
  // Wordmark halves rendered in two colors. Brand name — never localized.
  buildPart: 'Build',
  hqPart: 'HQ',
  href: 'https://www.buildhq.app/?utm_source=nynjwc&utm_medium=banner&utm_campaign=in-app-ad',
  headline: 'Procore-grade jobsite reports, without the Procore price',
  subhead: 'Daily reports, photos, and AI summaries — flat $30/mo.',
  cta: 'See how it works',
  // Compact copy (Set D) for the minimal variant
  shortHeadline: 'Run your jobsite from one place',
  orange: '#FF6B35',
  ink: '#1A1A2E',
} as const;

// "Build" neutral + "HQ" orange, per BRAND.md. Brand wordmark — not localized.
function Wordmark({ buildColor, fontSize }: { buildColor: string; fontSize: number }) {
  return (
    <span style={{ fontFamily: '"Space Grotesk", -apple-system, system-ui, sans-serif', fontWeight: 700, fontSize }}>
      <span style={{ color: buildColor }}>{AD.buildPart}</span>
      <span style={{ color: AD.orange }}>{AD.hqPart}</span>
    </span>
  );
}

export function MockAd({ variant }: { variant: Variant }) {
  const { t } = useTranslation();
  if (variant === 'editorial') {
    return (
      <a
        href={AD.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'block',
          margin: '20px 24px',
          padding: '18px 0',
          borderTop: '1px solid #d8d2c8',
          borderBottom: '1px solid #d8d2c8',
          textDecoration: 'none',
        }}
      >
        <div
          style={{
            fontFamily: 'ui-monospace, monospace',
            fontSize: 9,
            letterSpacing: 1.6,
            color: '#a89e90',
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          {t('sponsored')} · {AD.brand}
        </div>
        <div
          style={{
            fontFamily: '"Instrument Serif", Georgia, serif',
            fontSize: 22,
            color: '#1a1612',
            lineHeight: 1.2,
            marginBottom: 6,
            fontStyle: 'italic',
          }}
        >
          {AD.headline}
        </div>
        <div
          style={{
            fontSize: 12,
            color: '#5a544c',
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            marginBottom: 8,
          }}
        >
          {AD.subhead}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: AD.orange }}>{AD.cta}</span>
          <span style={{ fontSize: 11, color: AD.orange }}>→</span>
        </div>
      </a>
    );
  }

  if (variant === 'minimal') {
    return (
      <a
        href={AD.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display: 'flex',
          margin: '14px 20px',
          padding: '12px 14px',
          border: '1px solid #f0ede8',
          borderRadius: 10,
          alignItems: 'center',
          gap: 12,
          textDecoration: 'none',
        }}
      >
        <img
          src="/buildhq-mark.svg"
          alt={AD.brand}
          width={36}
          height={36}
          style={{ borderRadius: 8, flexShrink: 0, display: 'block' }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <Wordmark buildColor="#0a0a0a" fontSize={13} />
            <span
              style={{
                fontSize: 9,
                fontWeight: 600,
                color: '#9a9590',
                background: '#f0ede8',
                padding: '1px 5px',
                borderRadius: 3,
                letterSpacing: 0.4,
              }}
            >
              {t('adLabel')}
            </span>
          </div>
          <div style={{ fontSize: 12, color: '#5a544c' }}>{AD.shortHeadline}</div>
        </div>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#9a9590" strokeWidth="2">
          <path d="M4 2l4 4-4 4" />
        </svg>
      </a>
    );
  }

  // sporty — dark filled card variant (BRAND.md: #1A1A2E bg, white text, orange CTA)
  return (
    <a
      href={AD.href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'block',
        margin: '14px 20px',
        background: AD.ink,
        color: '#FFFFFF',
        borderRadius: 16,
        overflow: 'hidden',
        position: 'relative',
        textDecoration: 'none',
        boxShadow: '0 8px 32px rgba(26,26,46,0.12)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          background: 'rgba(255,255,255,0.10)',
          color: 'rgba(255,255,255,0.72)',
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: 1,
          padding: '3px 7px',
          borderRadius: 6,
        }}
      >
        {t('sponsoredUpper')}
      </div>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
          <img
            src="/buildhq-mark.svg"
            alt={AD.brand}
            width={24}
            height={24}
            style={{ borderRadius: 6, display: 'block' }}
          />
          <Wordmark buildColor="#FFFFFF" fontSize={15} />
        </div>
        <div
          style={{
            fontFamily: '"Space Grotesk", -apple-system, system-ui, sans-serif',
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.25,
            letterSpacing: -0.2,
            marginBottom: 6,
          }}
        >
          {AD.headline}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'rgba(255,255,255,0.66)',
            lineHeight: 1.45,
            marginBottom: 14,
          }}
        >
          {AD.subhead}
        </div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: '"Space Grotesk", -apple-system, system-ui, sans-serif',
            fontSize: 13,
            fontWeight: 700,
            padding: '9px 14px',
            background: AD.orange,
            color: '#FFFFFF',
            borderRadius: 12,
          }}
        >
          {AD.cta} →
        </div>
      </div>
    </a>
  );
}
